/**
 * Resolve widget bootstrap settings from, in order:
 *  1. window.ChatZouSettings (set by loader.js)
 *  2. URL query params (?siteKey=&apiUrl=)
 *  3. Build-time VITE_* env
 *
 * Returns null when siteKey is missing — the caller renders an "invalid
 * install" state so the host developer sees what went wrong.
 */
import type { WidgetSettings } from "@/types";

export function resolveSettings(): WidgetSettings | null {
  const fromWindow =
    typeof window !== "undefined" ? window.ChatZouSettings : undefined;

  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const siteKey =
    fromWindow?.siteKey ?? params.get("siteKey") ?? undefined;
  if (!siteKey) return null;

  const apiUrl =
    fromWindow?.apiUrl ??
    params.get("apiUrl") ??
    import.meta.env.VITE_API_URL ??
    "http://localhost:3001";

  return {
    siteKey,
    apiUrl: normalizeUrl(apiUrl),
    identity: fromWindow?.identity,
    identityHash: fromWindow?.identityHash,
  };
}

function normalizeUrl(url: string): string {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

export const VISITOR_STORAGE_KEY = "chatzou.visitor.id";
export const SESSION_STORAGE_KEY = "chatzou.session.token";

export function readVisitorId(): string | undefined {
  try {
    return localStorage.getItem(VISITOR_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function writeVisitorId(id: string): void {
  try {
    localStorage.setItem(VISITOR_STORAGE_KEY, id);
  } catch {
    // storage unavailable (private mode / blocked) — continue anonymously
  }
}
